import { CheckCircleOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, App, Badge, Button, Card, DatePicker, Descriptions, Drawer, Flex, Input, Segmented,
  Statistic, Table, Tag, Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  fetchBatches, fetchBridgeStatus, fetchCashSaleDebtor, fetchDocuments, fetchUnmatchedCustomers, mapDebtor,
  previewHandover, releaseHandover, searchDebtors, setCashSaleDebtor,
  type BatchRow, type DebtorMatch, type DocumentRow, type KeyByHandDocument, type PostingState,
  type ReleasedBatch,
  type UnmatchedCustomer,
} from './api';
import { CodeSettings } from './CodeSettings';

const { Text, Title } = Typography;

/**
 * A unit price is stored to four decimals. `formatMoney` shows cents, which
 * would turn 45.1234 into 45.12 on the one screen where finance keys the price.
 */
function formatUnitPrice(value: string): string {
  const [whole = '0', fraction = ''] = value.split('.');
  if (fraction.length <= 2) return formatMoney(value);
  // formatMoney pads whole to "RM 45.00"; keep its symbol and grouping, swap the cents.
  return `${formatMoney(whole).replace(/\.00$/, '')}.${fraction}`;
}

/**
 * Renders one field of the agent's self-report.
 *
 * `lastStatus` is stored whole so a newer agent's field is not silently
 * dropped — which means it can contain anything. `String(value)` on an object
 * produces "[object Object]", a perfectly valid string that would sit on the
 * screen looking like data. Anything that is not already a primitive shows as
 * unknown instead.
 */
function statusField(status: Record<string, unknown> | null, key: string): string {
  const value = status?.[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '—';
}

/**
 * The Accounts desk (CLAUDE.md §13a).
 *
 * Answers three questions that currently have no home anywhere in the product:
 *
 *   1. Is the agent on the AutoCount machine alive? One that dies quietly is
 *      invisible until a reconciliation mysteriously finds nothing.
 *   2. What have we issued that finance has not been given yet? §8 requires
 *      such a document to be VISIBLE, never silently absent.
 *   3. What did we hand over, and did it reach the books?
 */
export function AccountsPage(): ReactNode {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'documents' | 'batches'>('documents');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().add(1, 'day').startOf('day'),
  ]);
  const [released, setReleased] = useState<ReleasedBatch | null>(null);
  const [matching, setMatching] = useState<UnmatchedCustomer | null>(null);
  const [matchingCashSale, setMatchingCashSale] = useState(false);
  const [search, setSearch] = useState('');

  const bridge = useQuery({
    queryKey: ['accounting', 'bridge'],
    queryFn: fetchBridgeStatus,
    // The point of a heartbeat is that it stops. Polling means the screen
    // notices without somebody pressing refresh.
    refetchInterval: 60_000,
  });
  const documents = useQuery({ queryKey: ['accounting', 'documents'], queryFn: fetchDocuments });
  const batches = useQuery({ queryKey: ['accounting', 'batches'], queryFn: fetchBatches });

  const unmatched = useQuery({
    queryKey: ['accounting', 'unmatched'],
    queryFn: fetchUnmatchedCustomers,
  });

  const cashSale = useQuery({ queryKey: ['accounting', 'cash-sale'], queryFn: fetchCashSaleDebtor });

  const debtorResults = useQuery({
    queryKey: ['accounting', 'debtor-search', search],
    queryFn: () => searchDebtors(search),
    enabled: search.trim().length >= 2,
  });

  const match = useMutation({
    mutationFn: ({ customerUid, debtorCode }: { customerUid: string; debtorCode: string }) =>
      mapDebtor(customerUid, debtorCode),
    onSuccess: () => {
      message.success(t('accounting.matched'));
      setMatching(null);
      setSearch('');
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (e: unknown) => {
      message.error(e instanceof ApiError ? e.message : t('accounting.matchFailed'));
    },
  });

  const setCash = useMutation({
    mutationFn: (debtorCode: string) => setCashSaleDebtor(debtorCode),
    onSuccess: () => {
      message.success(t('accounting.cashSaleSet'));
      setMatchingCashSale(false);
      setSearch('');
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (e: unknown) => {
      message.error(e instanceof ApiError ? e.message : t('accounting.matchFailed'));
    },
  });

  const preview = useQuery({
    queryKey: ['accounting', 'preview', range[0].toISOString(), range[1].toISOString()],
    queryFn: () => previewHandover(range[0].toISOString(), range[1].toISOString()),
  });

  const release = useMutation({
    mutationFn: () => releaseHandover(range[0].toISOString(), range[1].toISOString()),
    onSuccess: (batch) => {
      setReleased(batch);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    // The API's refusals say exactly what would have happened — an empty
    // batch, or another batch having claimed the documents. Rewording them
    // here would lose that.
    onError: (e: unknown) => {
      message.error(e instanceof ApiError ? e.message : t('accounting.releaseFailed'));
    },
  });

  const stateTag = (state: PostingState): ReactNode => {
    const map: Record<PostingState, { color: string; label: string }> = {
      PENDING: { color: 'warning', label: t('accounting.pending') },
      HANDED_OVER: { color: 'processing', label: t('accounting.handedOver') },
      CONFIRMED_POSTED: { color: 'success', label: t('accounting.confirmed') },
      VARIANCE: { color: 'error', label: t('accounting.variance') },
    };
    return <Tag color={map[state].color}>{map[state].label}</Tag>;
  };

  /** "To paste: RM x · To key by hand: RM y". Both figures come from the API; money is never subtracted here. */
  const splitTotals = (pasted: string, keyed: string): string =>
    t('accounting.splitTotals', { paste: formatMoney(pasted), keyed: formatMoney(keyed) });

  const copy = (text: string): void => {
    void navigator.clipboard.writeText(text).then(
      () => { message.success(t('accounting.copied')); },
      () => { message.error(t('accounting.copyFailed')); },
    );
  };

  return (
    <Flex vertical gap={16}>
      {/* ── the bridge ─────────────────────────────────────────── */}
      {(bridge.data ?? []).map((agent) => {
        const stale =
          agent.lastHeartbeatAt === null
          || dayjs().diff(dayjs(agent.lastHeartbeatAt), 'minute') > 5;
        return (
          <Card key={agent.name} size="small">
            <Flex justify="space-between" align="center" wrap gap={12}>
              <Flex align="center" gap={10}>
                <Badge status={agent.healthy && !stale ? 'success' : 'error'} />
                <Flex vertical gap={0}>
                  <Text strong>{t('accounting.agent', { name: agent.name })}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {agent.lastHeartbeatAt === null
                      ? t('accounting.neverReported')
                      : t('accounting.lastHeard', {
                          when: dayjs(agent.lastHeartbeatAt).format('D MMM HH:mm'),
                        })}
                  </Text>
                </Flex>
              </Flex>
              <Flex gap={20} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('accounting.readingBook', {
                    book: statusField(agent.status, 'database'),
                  })}
                </Text>
                <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                  {t('accounting.seesUnits', { n: statusField(agent.status, 'unitCount') })}
                </Text>
                <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                  {t('accounting.seesInvoices', {
                    n: statusField(agent.status, 'arInvoiceCount'),
                  })}
                </Text>
              </Flex>
            </Flex>

            {/* A stale heartbeat is the failure this card exists to surface.
                An unhealthy agent explains itself in `error`. */}
            {(stale || !agent.healthy) && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 12 }}
                message={stale ? t('accounting.agentSilent') : t('accounting.agentUnhealthy')}
                description={
                  typeof agent.status?.['error'] === 'string'
                    ? agent.status['error']
                    : t('accounting.agentSilentBody')
                }
              />
            )}
          </Card>
        );
      })}

      {/* ── the debtor unnamed counter sales are filed against ─── */}
      <Card size="small" title={t('accounting.cashSaleTitle')}>
        <Flex justify="space-between" align="center" gap={12} wrap>
          <Flex vertical gap={2}>
            {cashSale.data?.debtorCode != null ? (
              <Text className="tabular">{cashSale.data.debtorCode}</Text>
            ) : (
              <Text type="warning">{t('accounting.cashSaleUnset')}</Text>
            )}
            {(unmatched.data?.cashSales ?? 0) > 0 && (
              <Tag color="warning">{t('accounting.cashSalesWaiting', { count: unmatched.data?.cashSales ?? 0 })}</Tag>
            )}
          </Flex>
          <Button type="primary" onClick={() => { setMatchingCashSale(true); setSearch(''); }}>
            {cashSale.data?.debtorCode != null ? t('accounting.cashSaleChange') : t('accounting.cashSaleSetButton')}
          </Button>
        </Flex>
      </Card>

      <CodeSettings />

      {/* ── customers with nowhere to file their documents ─────── */}
      {(unmatched.data?.customers ?? []).length > 0 && (
        <Card
          size="small"
          title={
            <Flex align="center" gap={8}>
              <WarningOutlined />
              {t('accounting.unmatchedTitle', { count: unmatched.data?.customers.length ?? 0 })}
            </Flex>
          }
        >
          <Flex vertical gap={8}>
            <Text type="secondary">{t('accounting.unmatchedBody')}</Text>
            {(unmatched.data?.customers ?? []).slice(0, 10).map((c) => (
              <Flex key={c.uid} justify="space-between" align="center" gap={12}>
                <Flex gap={8} align="center">
                  <Text>{c.name}</Text>
                  <Tag color="warning">
                    {t('accounting.docsWaiting', { count: c.documents })}
                  </Tag>
                </Flex>
                <Button size="small" type="primary" onClick={() => { setMatching(c); setSearch(''); }}>
                  {t('accounting.matchTo')}
                </Button>
              </Flex>
            ))}
          </Flex>
        </Card>
      )}

      {/* ── handover ───────────────────────────────────────────── */}
      <Card size="small" title={t('accounting.handover')}>
        <Flex vertical gap={12}>
          <Flex gap={12} align="flex-end" wrap>
            <Flex vertical gap={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('accounting.period')}
              </Text>
              <DatePicker.RangePicker
                value={range}
                allowClear={false}
                onChange={(values) => {
                  if (values?.[0] != null && values[1] != null) {
                    setRange([values[0].startOf('day'), values[1].endOf('day')]);
                  }
                }}
              />
            </Flex>

            <Statistic
              title={t('accounting.readyToHand')}
              value={preview.data?.documentCount ?? 0}
              loading={preview.isLoading}
            />
            {(preview.data?.keyByHand ?? []).length > 0 && (
              <Flex vertical gap={4}>
                <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                  {t('accounting.toKeyByHand', { count: preview.data?.keyByHand.length ?? 0 })}
                </Tag>
                <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                  {splitTotals(preview.data?.pastedTotal ?? '0', preview.data?.keyByHandTotal ?? '0')}
                </Text>
              </Flex>
            )}
            <Statistic
              title={t('accounting.receiptsTotal')}
              value={formatMoney(preview.data?.receiptsTotal ?? '0')}
              loading={preview.isLoading}
            />
            <Statistic
              title={t('accounting.creditNotesTotal')}
              value={formatMoney(preview.data?.creditNotesTotal ?? '0')}
              loading={preview.isLoading}
            />
            <Statistic
              title={t('accounting.refundsTotal')}
              value={formatMoney(preview.data?.refundsTotal ?? '0')}
              loading={preview.isLoading}
            />
            <Statistic
              title={t('accounting.value')}
              value={formatMoney(preview.data?.totalAmount ?? '0')}
              loading={preview.isLoading}
            />

            <Button
              type="primary"
              size="large"
              loading={release.isPending}
              disabled={(preview.data?.documentCount ?? 0) === 0}
              onClick={() => {
                // Releasing is irreversible — §8 makes a batch immutable and a
                // correction a NEW batch. Worth one confirmation.
                modal.confirm({
                  title: t('accounting.releaseTitle'),
                  content: t('accounting.releaseBody', {
                    count: preview.data?.documentCount ?? 0,
                  }),
                  okText: t('accounting.release'),
                  onOk: () => { release.mutate(); },
                });
              }}
            >
              {t('accounting.release')}
            </Button>
          </Flex>

          {/* Excluded documents are shown BEFORE the release, which is the
              whole reason preview exists — an unmapped debtor is fixable now
              and unfixable once the batch is frozen. */}
          {(preview.data?.excluded ?? []).length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('accounting.excludedTitle', {
                count: preview.data?.excluded.length ?? 0,
              })}
              description={
                <Flex vertical gap={2}>
                  <Text type="secondary">{t('accounting.excludedBody')}</Text>
                  {(preview.data?.excluded ?? []).slice(0, 8).map((e) => (
                    <Text key={e.docNo} className="tabular" style={{ fontSize: 12 }}>
                      {e.docNo} — {t(`accounting.reason.${e.reason}`, e.reason)}
                    </Text>
                  ))}
                </Flex>
              }
            />
          )}
        </Flex>
      </Card>

      <Segmented
        block
        value={tab}
        onChange={(value) => { setTab(value as 'documents' | 'batches'); }}
        options={[
          { value: 'documents', label: t('accounting.documents') },
          { value: 'batches', label: t('accounting.batches') },
        ]}
      />

      {tab === 'documents' ? (
        <Table<DocumentRow>
          size="middle"
          loading={documents.isLoading}
          dataSource={documents.data ?? []}
          rowKey={(row) => row.uid}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: t('accounting.docNo'),
              dataIndex: 'docNo',
              render: (v: string) => <span className="tabular">{v}</span>,
            },
            {
              title: t('accounting.date'),
              dataIndex: 'docDate',
              render: (v: string) => <span className="tabular">{formatDate(v)}</span>,
            },
            {
              title: t('accounting.debtor'),
              dataIndex: 'debtorCode',
              render: (v: string | null) =>
                v === null ? (
                  // The reason a document cannot be handed over. Named, not blank.
                  <Tag color="warning">{t('accounting.unmapped')}</Tag>
                ) : (
                  <span className="tabular">{v}</span>
                ),
            },
            {
              title: t('accounting.amount'),
              dataIndex: 'totalAmount',
              align: 'right' as const,
              render: (v: string) => <span className="tabular">{formatMoney(v)}</span>,
            },
            {
              title: t('accounting.state'),
              dataIndex: 'state',
              render: (v: PostingState) => stateTag(v),
            },
            { title: t('accounting.batch'), dataIndex: 'batchNo' },
            {
              title: t('accounting.inAutocount'),
              dataIndex: 'autocountDocNo',
              render: (v: string | null, row: DocumentRow) =>
                row.varianceReason !== null ? (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {row.varianceReason}
                  </Text>
                ) : (
                  <span className="tabular">{v ?? '—'}</span>
                ),
            },
          ]}
          locale={{ emptyText: t('accounting.noDocuments') }}
        />
      ) : (
        <Table<BatchRow>
          size="middle"
          loading={batches.isLoading}
          dataSource={batches.data ?? []}
          rowKey={(row) => row.uid}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: t('accounting.batch'),
              dataIndex: 'batchNo',
              render: (v: string) => <span className="tabular">{v}</span>,
            },
            {
              title: t('accounting.released'),
              dataIndex: 'releasedAt',
              render: (v: string | null) => (
                <span className="tabular">{v === null ? '—' : dayjs(v).format('D MMM HH:mm')}</span>
              ),
            },
            { title: t('accounting.by'), dataIndex: 'releasedBy' },
            {
              title: t('accounting.documents'),
              dataIndex: 'documentCount',
              align: 'right' as const,
              render: (v: number) => <span className="tabular">{v}</span>,
            },
            {
              title: t('accounting.amount'),
              dataIndex: 'totalAmount',
              align: 'right' as const,
              render: (v: string) => <span className="tabular">{formatMoney(v)}</span>,
            },
            {
              title: t('accounting.reconciled'),
              render: (_: unknown, row: BatchRow) => (
                <Flex gap={4}>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {row.confirmed}
                  </Tag>
                  {row.variance > 0 && (
                    <Tag icon={<WarningOutlined />} color="error">
                      {row.variance}
                    </Tag>
                  )}
                </Flex>
              ),
            },
          ]}
          locale={{ emptyText: t('accounting.noBatches') }}
        />
      )}

      {/* ── matching a customer to a debtor ────────────────────── */}
      <Drawer
        className="nks-action-drawer"
        width={480}
        open={matching !== null || matchingCashSale}
        title={matchingCashSale ? t('accounting.cashSaleTitle') : t('accounting.matchTitle', { name: matching?.name ?? '' })}
        onClose={() => { setMatching(null); setMatchingCashSale(false); setSearch(''); }}
      >
        <Flex vertical gap={12}>
          <Text type="secondary">{t('accounting.matchHint')}</Text>
          <Input.Search
            size="large"
            autoFocus
            allowClear
            placeholder={t('accounting.searchDebtors')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
          />

          {search.trim().length < 2 ? (
            <Text type="secondary">{t('accounting.typeToSearch')}</Text>
          ) : (
            <Flex vertical gap={6}>
              {debtorResults.isLoading && <Text type="secondary">{t('accounting.searching')}</Text>}
              {(debtorResults.data ?? []).length === 0 && !debtorResults.isLoading && (
                <Text type="secondary">{t('accounting.noDebtors')}</Text>
              )}
              {(debtorResults.data ?? []).map((d: DebtorMatch) => (
                <Flex
                  key={d.debtorCode}
                  justify="space-between"
                  align="center"
                  gap={8}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid var(--line)',
                    borderRadius: 6,
                  }}
                >
                  <Flex vertical gap={0}>
                    <Text>{d.companyName}</Text>
                    <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                      {d.debtorCode}
                      {d.registerNoLast4 !== null && ` · …${d.registerNoLast4}`}
                    </Text>
                  </Flex>
                  <Button
                    size="small"
                    type="primary"
                    // One customer, one debtor. Two sharing a code would merge
                    // two people's AR ageing inside NKS's books.
                    disabled={d.taken}
                    loading={match.isPending || setCash.isPending}
                    onClick={() => {
                      if (matchingCashSale) { setCash.mutate(d.debtorCode); return; }
                      if (matching === null) return;
                      match.mutate({ customerUid: matching.uid, debtorCode: d.debtorCode });
                    }}
                  >
                    {d.taken ? t('accounting.alreadyTaken') : t('accounting.useThis')}
                  </Button>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      </Drawer>

      {/* ── the file finance pastes ────────────────────────────── */}
      <Drawer
        className="nks-action-drawer"
        width={640}
        open={released !== null}
        title={t('accounting.batchReady', { batchNo: released?.batchNo ?? '' })}
        onClose={() => { setReleased(null); }}
      >
        {released !== null && (
          <Flex vertical gap={16}>
            {/* A batch of only key-by-hand documents has nothing to paste, so the
                paste settings and Copy button would copy a bare header row. */}
            {released.documentCount - released.keyByHand.length > 0 && (
            <>
            {/* The settings live WITH the file. A procedure document is not
                open when somebody is pasting at 6pm, and both of these are
                correctness-critical. */}
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={t('accounting.where')}>
                <Text className="tabular">{released.instructions.menuPath}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('accounting.dateFormat')}>
                <Tag color="processing">{released.instructions.dateFormat}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('accounting.duplicates')}>
                <Tag color="error">{released.instructions.duplicateAction}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('accounting.duplicatesWhy')}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Flex justify="space-between" align="center">
              <Title level={5} style={{ margin: 0 }}>
                {t('accounting.rows', { count: released.documentCount - released.keyByHand.length })}
              </Title>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => { copy(released.clipboardText); }}
              >
                {t('accounting.copyForPaste')}
              </Button>
            </Flex>

            <Alert type="info" showIcon message={t('accounting.pasteHint')} />

            <pre
              style={{
                maxHeight: 320,
                overflow: 'auto',
                fontSize: 11,
                padding: 12,
                background: 'var(--surface-sunken)',
                border: '1px solid var(--line)',
                borderRadius: 6,
              }}
            >
              {released.clipboardText}
            </pre>
            </>
            )}

            {released.keyByHand.length > 0 && (
              <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                {splitTotals(released.pastedTotal, released.keyByHandTotal)}
              </Text>
            )}
            <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
              {t('accounting.receiptsAndCreditNotes', {
                receipts: formatMoney(released.receiptsTotal),
                creditNotes: formatMoney(released.creditNotesTotal),
                refunds: formatMoney(released.refundsTotal),
              })}
            </Text>

            {/* Parts-bearing documents cannot be pasted: AutoCount's A/R import
                has no item lines, so only a Sales Invoice moves its stock. */}
            {released.keyByHand.length > 0 && (
              <Flex vertical gap={12}>
                <Title level={5} style={{ margin: 0 }}>
                  {t('accounting.keyByHandHeading', { count: released.keyByHand.length })}
                </Title>
                <Alert type="info" showIcon message={t('accounting.keyByHandBody')} description={t('accounting.keyByHandWhy')} />
                {released.keyByHand.map((d: KeyByHandDocument) => (
                  <Card key={d.docNo} size="small">
                    <Flex vertical gap={8}>
                      <Flex justify="space-between" align="center" gap={8} wrap>
                        <Flex gap={8} align="baseline" wrap>
                          <Tag style={{ marginInlineEnd: 0 }}>{t(`accounting.docType.${d.docType}`)}</Tag>
                          <Text strong className="tabular">{d.docNo}</Text>
                          {/* A hand-keyed Sales Invoice defaults to today in AutoCount;
                              a late keying would land in the wrong period. */}
                          <Text className="tabular">{formatDate(d.docDate)}</Text>
                        </Flex>
                        <Text strong className="tabular">{formatMoney(d.totalAmount)}</Text>
                      </Flex>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('accounting.keyByHandDebtorLine', { code: d.debtorCode })}
                      </Text>
                      {(d.docType === 'RECEIPT' || d.docType === 'REFUND' || d.docType === 'CREDIT_NOTE') && (
                        <Flex vertical gap={2}>
                          {d.description !== null && d.description !== '' && <Text>{d.description}</Text>}
                          {d.docType !== 'CREDIT_NOTE' && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {[
                                d.method === null ? null : t(`till.method.${d.method}`, { defaultValue: d.method }),
                                d.reference,
                              ].filter((v): v is string => v !== null && v !== '').join(' · ')}
                            </Text>
                          )}
                          {d.docType === 'RECEIPT' && d.settles !== null && (
                            <Text type="secondary">{t('accounting.settles', { invoiceNo: d.settles })}</Text>
                          )}
                          {d.docType === 'REFUND' && d.settles !== null && (
                            <Text type="secondary">{t('accounting.settlesRefund', { no: d.settles })}</Text>
                          )}
                        </Flex>
                      )}
                      {(d.docType === 'INVOICE' || d.docType === 'REFUND') && d.knockOff.length > 0 && (
                        <Text>{t('accounting.knockOff', { receipts: d.knockOff.join(', ') })}</Text>
                      )}
                      {d.promotions.map((p, i) => (
                        <Text key={`${d.docNo}-${String(i)}`} type="warning">
                          {t('accounting.promotion', { amount: formatMoney(p.amount), line: p.line })}
                        </Text>
                      ))}
                      {d.lines.length > 0 && <Table
                        size="small"
                        pagination={false}
                        rowKey={(_, i) => `${d.docNo}-${String(i)}`}
                        dataSource={d.lines}
                        scroll={{ x: 'max-content' }}
                        columns={[
                          { title: t('accounting.lineSku'), dataIndex: 'skuCode', render: (v: string) => <span className="tabular">{v === '' ? '—' : v}</span> },
                          { title: t('accounting.lineDescription'), dataIndex: 'description' },
                          { title: t('accounting.lineQty'), dataIndex: 'qty', align: 'right' as const, render: (v: string) => <span className="tabular">{v}</span> },
                          { title: t('accounting.linePrice'), dataIndex: 'unitPrice', align: 'right' as const, render: (v: string) => <span className="tabular">{formatUnitPrice(v)}</span> },
                          { title: t('accounting.lineDiscount'), dataIndex: 'discountAmount', align: 'right' as const, render: (v: string) => <span className="tabular">{formatMoney(v)}</span> },
                          { title: t('accounting.lineTotal'), dataIndex: 'lineTotal', align: 'right' as const, render: (v: string) => <span className="tabular">{formatMoney(v)}</span> },
                        ]}
                      />}
                    </Flex>
                  </Card>
                ))}
              </Flex>
            )}
          </Flex>
        )}
      </Drawer>
    </Flex>
  );
}
